import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChangeDetectionStrategy, Component, OnChanges, SimpleChanges, computed, inject, input, output, signal } from '@angular/core';
import { AbstractControl, ReactiveFormsModule } from '@angular/forms';
import { map, startWith } from 'rxjs';

import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';

import {
  ADDRESS_FIELD_VALIDATION_MESSAGES,
  ADDRESS_POSTAL_CODE_PATTERN,
} from '@shared/utils/address-form.utils';
import { FormFieldComponent } from '@shared/components/form/form-field/form-field.component';
import { FormFieldControlDirective } from '@shared/components/form/form-field/form-field-control.directive';
import { FormFieldPrimengSelectDirective } from '@shared/components/form/form-field/form-field-primeng-select.directive';
import { FormModalShellComponent } from '@shared/components/overlay/form-modal-shell/form-modal-shell.component';
import {
  resolveControlErrorMessage,
  ValidationMessageMap,
} from '@shared/utils/forms/control-error-message.util';
import { shouldShowControlError } from '@shared/utils/forms/control-error-visibility.util';

import {
  areInvestmentLocationsEqual,
  InvestmentLocationData,
  toInvestmentLocationData,
} from '@features/investment/utils/investment-location.utils';
import {
  InvestmentLocationFormValue,
  InvestmentLocationInputModalFacade,
} from './investment-location-input-modal.facade';

@Component({
  selector: 'app-investment-location-input-modal',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    InputTextModule,
    SelectModule,
    FormModalShellComponent,
    FormFieldComponent,
    FormFieldControlDirective,
    FormFieldPrimengSelectDirective,
  ],
  providers: [InvestmentLocationInputModalFacade],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './investment-location-input-modal.component.html',
  styleUrl: './investment-location-input-modal.component.scss',
})
export class InvestmentLocationInputModalComponent implements OnChanges {
  private static nextFormId = 0;

  readonly visible = input(false);
  readonly initialData = input<InvestmentLocationData | null>(null);
  readonly saving = input(false);

  readonly visibleChange = output<boolean>();
  readonly hidden = output<void>();
  readonly facade = inject(InvestmentLocationInputModalFacade);
  readonly saveRequested = output<InvestmentLocationData>();
  readonly submitAttempted = signal(false);
  readonly formId = `investment-location-modal-form-${InvestmentLocationInputModalComponent.nextFormId++}`;
  private readonly normalizedInitialValue = signal<InvestmentLocationData | null>(null);

  private readonly currentFormValue = signal<InvestmentLocationFormValue>(
    this.facade.form.getRawValue() as InvestmentLocationFormValue
  );

  readonly hasPendingChanges = computed(() =>
    !areInvestmentLocationsEqual(
      this.facade.buildLocationDataFromValue(this.currentFormValue()),
      this.normalizedInitialValue()
    )
  );
  readonly voivodeshipOptions = this.facade.voivodeshipOptions;
  readonly postalCodePattern = ADDRESS_POSTAL_CODE_PATTERN.source;

  constructor() {
    this.facade.form.valueChanges
      .pipe(
        startWith(this.facade.form.getRawValue()),
        map(() => this.facade.form.getRawValue() as InvestmentLocationFormValue),
        takeUntilDestroyed()
      )
      .subscribe((value) => {
        this.currentFormValue.set(value);
      });
  }

  onSaveRequested(): void {
    this.submitAttempted.set(true);
    const normalizedValue = this.commitNormalizedValues();
    if (this.facade.form.invalid) {
      this.facade.form.markAllAsTouched();
      return;
    }

    this.saveRequested.emit(normalizedValue);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['saving']) {
      this.syncControlDisabledState();
    }

    if (changes['visible']?.currentValue || (this.visible() && changes['initialData'])) {
      this.patchFormValue();
    }
  }

  onVisibleChange(nextVisible: boolean): void {
    this.visibleChange.emit(nextVisible);
  }

  onHidden(): void {
    this.hidden.emit();
  }

  addressFieldError(
    field: 'postalCode' | 'street' | 'city' | 'voivodeship' | 'district' | 'commune'
  ): string | null {
    return this.resolveErrorMessage(
      this.facade.form.controls[field],
      ADDRESS_FIELD_VALIDATION_MESSAGES[field]
    );
  }

  private commitNormalizedValues(): InvestmentLocationData {
    const normalizedValue = this.facade.buildLocationData();
    const normalizedFormValue = this.facade.toFormValue(normalizedValue);

    this.patchControlValue('postalCode', normalizedFormValue.postalCode);
    this.patchControlValue('city', normalizedFormValue.city);
    this.patchControlValue('street', normalizedFormValue.street);
    this.patchControlValue('voivodeship', normalizedFormValue.voivodeship);
    this.patchControlValue('district', normalizedFormValue.district);
    this.patchControlValue('commune', normalizedFormValue.commune);

    return normalizedValue;
  }

  private patchControlValue<K extends keyof InvestmentLocationFormValue>(
    controlName: K,
    value: InvestmentLocationFormValue[K]
  ): void {
    const control = this.facade.form.controls[controlName] as {
      value: InvestmentLocationFormValue[K];
      setValue(nextValue: InvestmentLocationFormValue[K]): void;
    };
    if (control.value === value) {
      return;
    }

    control.setValue(value);
  }

  private resolveErrorMessage(
    control: AbstractControl | null,
    messages: ValidationMessageMap
  ): string | null {
    if (!shouldShowControlError(control, { submitAttempted: this.submitAttempted() })) {
      return null;
    }

    return resolveControlErrorMessage(control, messages);
  }

  private patchFormValue(): void {
    const normalizedValue = toInvestmentLocationData(this.initialData()) ?? null;
    this.normalizedInitialValue.set(normalizedValue);
    this.submitAttempted.set(false);
    this.facade.patchInitialData(normalizedValue);
    this.currentFormValue.set(this.facade.form.getRawValue() as InvestmentLocationFormValue);
    this.syncControlDisabledState();
  }

  private syncControlDisabledState(): void {
    if (this.saving()) {
      this.facade.form.disable({ emitEvent: false });
      return;
    }

    this.facade.form.enable({ emitEvent: false });
  }
}
